import * as React from 'react'; // tslint:disable-line
import { FormProvider, ModalStateProvider, ActiveLangProvider, EntityProvider, AppStateProvider, EmbedComponentsProvider, SiteProvider, AvailableEntitiesProvider, KnowledgeHubStateProvider, HorizonTypesProvider, ViewProvider, PageEntityProvider, EnvProvider, EnvConfig } from '@shapeable/ui';
import { MenusProvider } from './providers/menus-provider';
import { toNumber } from 'lodash';
import { SiteMetadataProvider } from './providers/site-metadata-provider';
import { ImageProvider } from './providers/image-provider';
import { EventsProvider } from './providers/events-provider';
import { LinkProvider } from './providers/link-provider';
import { PeopleProvider } from './providers/people-provider';
import { PostsProvider } from './providers/posts-provider';
import { PromptTemplatesProvider } from './providers/prompt-templates-provider';
import { TopicsProvider } from './providers/topics-provider';
import { VideosProvider } from './providers/videos-provider';
import { FeedEntriesProvider } from './providers/feed-entries-provider';
import { ParallaxProvider } from 'react-scroll-parallax';
import { CitationsProvider } from './providers/citations-provider';
import { EntityTypeName, HasChildren } from '@shapeable/types';
import { PagesProvider } from './providers/pages-provider';
import { ChallengesProvider } from './providers/challenges-provider';
import { OrganisationsProvider } from './providers/organisations-provider';
import { AuthProvider } from './providers/auth-provider';
import { EconomiesProvider } from './providers/economies-provider';
import { OrganisationTypesProvider } from './providers/organisation-types-provider';
import { KeyIssuesProvider } from './providers/key-issues-provider';

const ENTITY_PROVIDERS: Partial<Record<EntityTypeName, React.FC<any>>> = {
  Challenge: ChallengesProvider,
  Citation: CitationsProvider,
  Economy: EconomiesProvider,
  Event: EventsProvider,
  FeedEntry: FeedEntriesProvider,
  KeyIssue: KeyIssuesProvider,
  HorizonType: HorizonTypesProvider,
  Organisation: OrganisationsProvider,
  OrganisationType: OrganisationTypesProvider,
  Page: PagesProvider,
  Person: PeopleProvider,
  Post: PostsProvider,
  Topic: TopicsProvider,
  Video: VideosProvider,
};

export const GatsbyProviders: React.FC<any> = (props) => {
  const { children, pageContext = {}, path } = props;

  // TODO - conditionally apply providers based on entity config (a list of which can be passed via page context - not much data)
  // Should also allow entities to be supported, but providers to be switched off if they will be overridden anyway (potentially huge waste of data).
  // Strategy - create an Entities Provider which takes the entity config from page Context and handles this.

  const { availableEntities = [] } = pageContext;

  // create a wrapping EntitiesProvider component based on what's available
  // console.log("AVAILABLE ENTITIES", availableEntities);



  const EntitiesProvider: React.FC<HasChildren> = ({ children }) => {
    return availableEntities.reduce((acc, entityTypeName) => {
      const Provider = ENTITY_PROVIDERS[entityTypeName];
      return Provider ? <Provider>{acc}</Provider> : acc;
    } , children);
  };

  const entityProviderValue = {
    ...pageContext,
    __path: pageContext.entityPath, // we need to use a different path for the entity provider, as the path is used by multi-lang sites to easily link to the entity, and should contain the correct path
    path: pageContext.path,
  };

  // provide env with webpack - this was moved from @shapeable/ui as it's not portable (Vite does not allow this, for example)
  const env: EnvConfig = {
    SITE_TITLE: process.env.SITE_TITLE,
    SITE_TITLE_TEMPLATE: process.env.SITE_TITLE_TEMPLATE,
    SITE_OPEN_GRAPH_TITLE_TEMPLATE: process.env.SITE_OPEN_GRAPH_TITLE_TEMPLATE,
    SITE_DESCRIPTION: process.env.SITE_DESCRIPTION,
    SITE_HEADLINE: process.env.SITE_HEADLINE,
    SITE_IMAGE: process.env.SITE_IMAGE,
    SITE_LOGO_NAVIGATION_URL: process.env.SITE_LOGO_NAVIGATION_URL,
    SITE_URL: process.env.SITE_URL,
    SITE_VIDEO: process.env.SITE_VIDEO,
    SITE_TYPE: process.env.SITE_TYPE,
    SITE_TWITTER: process.env.SITE_TWITTER,
    SITE_THREADS: process.env.SITE_THREADS,
    SITE_YOUTUBE: process.env.SITE_YOUTUBE,
    SITE_FACEBOOK: process.env.SITE_FACEBOOK,
    SITE_LINKEDIN: process.env.SITE_LINKEDIN,
    SITE_FLICKR: process.env.SITE_FLICKR,
    SITE_TIKTOK: process.env.SITE_TIKTOK,
    SITE_INSTAGRAM: process.env.SITE_INSTAGRAM,
    SITE_NAME: process.env.SITE_NAME,
    SITE_RECAPTCHA_KEY: process.env.SITE_RECAPTCHA_KEY,
    SITE_SIDEBAR_WIDTH: toNumber(process.env.SITE_SIDEBAR_WIDTH),
    SITE_SIDEBAR_MENU_WIDTH: toNumber(process.env.SITE_SIDEBAR_MENU_WIDTH),
    SITE_SIDEBAR_COLOR: process.env.SITE_SIDEBAR_COLOR,

    SITE_HEADER_HEIGHT_MOBILE: toNumber(process.env.SITE_HEADER_HEIGHT_MOBILE),
    SITE_HEADER_HEIGHT_TABLET: toNumber(process.env.SITE_HEADER_HEIGHT_TABLET),
    SITE_HEADER_HEIGHT_DESKTOP: toNumber(process.env.SITE_HEADER_HEIGHT_DESKTOP),

    ENABLE_LANG: process.env.ENABLE_LANG === 'true',
    DEFAULT_LANG: process.env.DEFAULT_LANG,
    LANGS_COMING_SOON: process.env.LANGS_COMING_SOON,

    MAX_WIDTH_CONTENT: process.env.MAX_WIDTH_CONTENT,
    MAX_WIDTH_FEATURE: process.env.MAX_WIDTH_FEATURE,
    MAX_WIDTH_EXPLORER: process.env.MAX_WIDTH_EXPLORER,

    PLATFORM_NAME: process.env.PLATFORM_NAME,
    PLATFORM_URL: process.env.PLATFORM_URL,
    
    ORG_NAME: process.env.ORG_NAME,

    CONTRIBUTE_BUTTON_LABEL: process.env.CONTRIBUTE_BUTTON_LABEL,
    DATA_VIZ_PANEL_HIDE_LABEL: process.env.DATA_VIZ_PANEL_HIDE_LABEL,
    PDF_DOWNLOAD_NAME: process.env.PDF_DOWNLOAD_NAME,

    DEBUG_ICON_LINK: process.env.DEBUG_ICON_LINK === 'true',
    DEBUG_ENTITIES: process.env.DEBUG_ENTITIES === 'true',
    DEBUG_EMBED_PROPS: process.env.DEBUG_EMBED_PROPS === 'true',
    DEBUG_SITE_VARS: process.env.DEBUG_SITE_VARS === 'true',
    DEBUG_PAGE_NAVIGATION: process.env.DEBUG_PAGE_NAVIGATION === 'true',
    DEBUG_LINEAR_NAVIGATION: process.env.DEBUG_LINEAR_NAVIGATION === 'true',

    FEED_ENTRY_GRID_TITLE: process.env.FEED_ENTRY_GRID_TITLE,
    PERSON_BANNER_LABEL: process.env.PERSON_BANNER_LABEL,

    SUPPORT_EMAIL: process.env.SUPPORT_EMAIL,
    CONTACT_EMAIL: process.env.CONTACT_EMAIL,
    HOME_PAGE_SLUG: process.env.HOME_PAGE_SLUG,
    MAIN_MENU_SLUG: process.env.MAIN_MENU_SLUG,
    
    GPT_SERVER: process.env.GPT_SERVER,

    PRIVACY_PATH: process.env.PRIVACY_PATH,
    TERMS_PATH: process.env.TERMS_PATH,

    GOOGLE_SITE_VERIFICATION_KEY: process.env.GOOGLE_SITE_VERIFICATION_KEY,

    ICON_LOGO_LABEL: process.env.ICON_LOGO_LABEL,
    ICON_LOGO_COLOR: process.env.ICON_LOGO_COLOR,

    LOGO_VERTICAL_OFFSET: toNumber(process.env.LOGO_VERTICAL_OFFSET || 0),
    LOGO_HORIZONTAL_OFFSET: toNumber(process.env.LOGO_HORIZONTAL_OFFSET || 0),
    LOGO_VERTICAL_OFFSET_MOBILE: toNumber(process.env.LOGO_VERTICAL_OFFSET_MOBILE || 0),
    LOGO_HORIZONTAL_OFFSET_MOBILE: toNumber(process.env.LOGO_HORIZONTAL_OFFSET_MOBILE || 0),
    LOGO_VERTICAL_OFFSET_TABLET: toNumber(process.env.LOGO_VERTICAL_OFFSET_TABLET || 0),
    LOGO_HORIZONTAL_OFFSET_TABLET: toNumber(process.env.LOGO_HORIZONTAL_OFFSET_TABLET || 0),
    LOGO_VERTICAL_OFFSET_DESKTOP: toNumber(process.env.LOGO_VERTICAL_OFFSET_DESKTOP || 0),
    LOGO_HORIZONTAL_OFFSET_DESKTOP: toNumber(process.env.LOGO_HORIZONTAL_OFFSET_DESKTOP || 0),
    LOGO_HEIGHT_MOBILE: toNumber(process.env.LOGO_HEIGHT_MOBILE || 0),
    LOGO_HEIGHT_TABLET: toNumber(process.env.LOGO_HEIGHT_TABLET || 0),
    LOGO_HEIGHT_DESKTOP: toNumber(process.env.LOGO_HEIGHT_DESKTOP || 0),

    LOGIN_LABEL: process.env.LOGIN_LABEL,
    LOGIN_BUTTON_LABEL: process.env.LOGIN_BUTTON_LABEL,
    PLATFORM_LOGIN_LABEL: process.env.PLATFORM_LOGIN_LABEL,
    LOGOUT_LABEL: process.env.LOGOUT_LABEL,

    SHOW_LOGIN: process.env.SHOW_LOGIN === 'true',
    SHOW_PLATFORM_LOGIN: process.env.SHOW_PLATFORM_LOGIN === 'true',
    SHOW_SEARCH: process.env.SHOW_SEARCH === 'true',
    SHOW_SHARE: process.env.SHOW_SHARE === 'true',
    SHOW_FOLLOW: process.env.SHOW_FOLLOW === 'true',
    SHOW_CONTACT_US: process.env.SHOW_CONTACT_US === 'true',
    SHOW_LINEAR_NAVIGATION: process.env.SHOW_LINEAR_NAVIGATION === 'true',

    FOOTER_MENU_SLUG: process.env.FOOTER_MENU_SLUG,
    FOOTER_SECONDARY_MENU_SLUG: process.env.FOOTER_SECONDARY_MENU_SLUG,
    GOOGLE_ANALYTICS_TRACKING_ID: process.env.GOOGLE_ANALYTICS_TRACKING_ID,

  };

  return (
    <EnvProvider value={env}>
    <AuthProvider>
    <AvailableEntitiesProvider value={availableEntities}>
    <ActiveLangProvider value={pageContext.lang}>
    <KnowledgeHubStateProvider>
    <ViewProvider>
    <AppStateProvider>
    <ModalStateProvider>
    <ParallaxProvider>
    <EmbedComponentsProvider>
    <FormProvider>
      <PromptTemplatesProvider>
      <EntitiesProvider>
      <MenusProvider>
        <SiteProvider value={pageContext.site}>
        <SiteMetadataProvider>
        <ImageProvider>
        <LinkProvider>
        <PageEntityProvider value={entityProviderValue}>
        <EntityProvider value={entityProviderValue}>
        {children}
        </EntityProvider>
        </PageEntityProvider>
        </LinkProvider>
        </ImageProvider>
        </SiteMetadataProvider>
        </SiteProvider>
      </MenusProvider>
      </EntitiesProvider>
      </PromptTemplatesProvider>
    </FormProvider>
    </EmbedComponentsProvider>
    </ParallaxProvider>
    </ModalStateProvider>
    </AppStateProvider>
    </ViewProvider>
    </KnowledgeHubStateProvider>    
    </ActiveLangProvider>
    </AvailableEntitiesProvider>
    </AuthProvider>
    </EnvProvider>
  );
}